
import Vue from "vue";
import { mapGetters, mapMutations } from "vuex";
import { GET_USER_DETAILS } from "@/store/modules/auth/constants";
import { ROOT_NOTIFICATION } from "@/store/modules/root/constants";
import {
  INIT_AI_INTERVIEW_INVITES,
  PERSON_INTERVIEW_INVITES
} from "@/store/modules/recruiter_ai_interviews/constants";
import { generate_acs_teams_token } from "@/store/apis/recruiter";
import { upsert_interview_report } from "@/store/apis/ml_interview";
import { AzureCommunicationTokenCredential } from "@azure/communication-common";
import { CallClient, LocalVideoStream, VideoStreamRenderer } from "@azure/communication-calling";

export default Vue.extend({
  name: "CandidateFinalInterviewSession",
  data() {
    return {
      meetingLink: "",
      sessionStatus: "Preparing call...",
      sessionError: "",
      hasRemoteVideo: false,
      callClient: null as any,
      callAgent: null as any,
      deviceManager: null as any,
      currentCall: null as any,
      localVideoStream: null as any,
      localRenderer: null as any,
      localView: null as any,
      remoteRenderers: [] as any[],
      isMuted: false,
      isCameraOn: false,
      isScreenSharing: false,
      localPreviewStreamType: "Video" as "Video" | "ScreenSharing",
      speechRecognitionSupported: false,
      recognition: null as any,
      transcriptEntries: [] as Array<{ speaker: string; text: string; timestamp: string }>,
      transcriptFlushTimer: null as ReturnType<typeof setTimeout> | null,
      currentInvite: null as any,
      candidateId: null as number | null,
      hasCompletedTranscriptUpsert: false,
      showProcessingDialog: false,
      pageExitFlushStarted: false,
      isJoiningCall: false
    };
  },
  computed: {
    ...mapGetters("auth", { user: GET_USER_DETAILS }),
    ...mapGetters("recruiter_ai_interviews", { personInvites: PERSON_INTERVIEW_INVITES })
  },
  methods: {
    ...mapMutations({ set_root_notification: ROOT_NOTIFICATION }),
    scheduleTranscriptFlush(this: any) {
      if (this.transcriptFlushTimer) {
        clearTimeout(this.transcriptFlushTimer);
      }
      this.transcriptFlushTimer = setTimeout(() => {
        this.flushTranscriptToDb();
      }, 2000);
    },
    async flushTranscriptToDb(this: any, force = false, statusOverride: string | null = null) {
      if (!this.candidateId || !this.transcriptEntries.length) return;
      if (!force && this.currentCall?.state !== "Connected") return;
      const computedStatus =
        statusOverride || (this.currentCall?.state === "Disconnected" ? "completed" : "in_progress");
      if (computedStatus === "completed" && this.hasCompletedTranscriptUpsert) return;
      if (computedStatus === "completed") {
        this.showProcessingDialog = true;
      }
      try {
        const activeJobId = Number(this.currentInvite?.job_id || 0) || null;
        await upsert_interview_report({
          candidate_id: this.candidateId,
          interview_type: "person",
          job_id: activeJobId,
          human_transcript: this.transcriptEntries,
          status: computedStatus
        });
        if (computedStatus === "completed") {
          this.hasCompletedTranscriptUpsert = true;
        }
      } catch (_e) {
        // keep interview experience smooth; we retry on the next flush
      } finally {
        if (computedStatus !== "completed") {
          this.showProcessingDialog = false;
        }
      }
    },
    initSpeechRecognition(this: any) {
      const SpeechRecognitionCtor =
        (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
      if (!SpeechRecognitionCtor) {
        this.speechRecognitionSupported = false;
        return;
      }

      this.speechRecognitionSupported = true;
      this.recognition = new SpeechRecognitionCtor();
      this.recognition.continuous = true;
      this.recognition.interimResults = false;
      this.recognition.lang = "en-US";

      this.recognition.onresult = (event: any) => {
        const nextEntries: Array<{ speaker: string; text: string; timestamp: string }> = [];
        for (let i = event.resultIndex; i < event.results.length; i += 1) {
          const result = event.results[i];
          if (!result?.isFinal) continue;
          const text = (result[0]?.transcript || "").trim();
          if (!text) continue;
          nextEntries.push({
            // Browser speech recognition cannot reliably identify who is speaking
            // in a live call (candidate vs interviewer), so keep this neutral.
            speaker: "unknown",
            text,
            timestamp: new Date().toISOString()
          });
        }
        if (nextEntries.length) {
          this.transcriptEntries = [...this.transcriptEntries, ...nextEntries];
          this.scheduleTranscriptFlush();
        }
      };

      this.recognition.onerror = () => {
        // browser handles microphone permission and runtime errors; no noisy toast
      };

      this.recognition.onend = () => {
        if (this.currentCall?.state === "Connected") {
          try {
            this.recognition.start();
          } catch (_e) {
            // ignore auto-restart issues
          }
        }
      };
    },
    startSpeechRecognition(this: any) {
      if (!this.speechRecognitionSupported || !this.recognition) return;
      try {
        this.recognition.start();
      } catch (_e) {
        // already started or unavailable
      }
    },
    stopSpeechRecognition(this: any) {
      try {
        this.recognition?.stop?.();
      } catch (_e) {
        // ignore stop errors
      }
    },
    async leaveSession(this: any) {
      this.stopSpeechRecognition();
      this.persistTranscriptOnPageExit();
      await this.flushTranscriptToDb(true, "completed");
      await this.cleanupCallResources();
      const inviteId =
        String(this.currentInvite?.id || "").trim() ||
        String(this.$route?.query?.inviteId || "").trim();
      if (inviteId) {
        this.$router.push({
          name: "candidate-final-interview-details",
          params: { inviteId }
        });
        return;
      }
      this.$router.push({ name: "candidate-final-interview" });
    },
    persistTranscriptOnPageExit(this: any) {
      if (this.pageExitFlushStarted) return;
      if (!this.candidateId || !this.transcriptEntries.length) return;
      this.pageExitFlushStarted = true;
      try {
        const token = localStorage.getItem("token") || "";
        const activeJobId = Number(this.currentInvite?.job_id || 0) || null;
        const payload = {
          candidate_id: this.candidateId,
          interview_type: "person",
          job_id: activeJobId,
          human_transcript: this.transcriptEntries,
          status: "completed"
        };
        const lang = (this as any).$i18n?.locale || "en";
        fetch("/api/recruiter/interview/ml/report", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            token,
            domain: window.location.href,
            lang
          },
          body: JSON.stringify(payload),
          keepalive: true
        }).catch(() => {
          // ignore best-effort failures on page exit
        });
      } catch (_e) {
        // ignore best-effort failures on page exit
      }
    },
    onPageExit(this: any) {
      this.stopSpeechRecognition();
      this.persistTranscriptOnPageExit();
    },
    async cleanupCallResources(this: any) {
      try {
        if (this.currentCall && typeof this.currentCall.hangUp === "function") {
          await this.currentCall.hangUp();
        }
      } catch (_e) {
        // ignore call hangup errors
      }
      this.currentCall = null;

      // Keep a shared ACS agent/client for this browser tab to avoid
      // "call agent already exists" races on quick re-join.
      this.callAgent = null;
      this.callClient = null;
    },
    clearLocalPreview(this: any) {
      const container = this.$refs.localContainer as HTMLElement | undefined;
      if (container) {
        container.innerHTML = "";
      }
      try {
        this.localView?.dispose?.();
      } catch (_e) {
        // ignore local view cleanup error
      }
      try {
        this.localRenderer?.dispose?.();
      } catch (_e) {
        // ignore local renderer cleanup error
      }
      this.localView = null;
      this.localRenderer = null;
    },
    clearRemotePreview(this: any) {
      const container = this.$refs.remoteContainer as HTMLElement | undefined;
      if (container) {
        container.innerHTML = "";
      }
      this.remoteRenderers.forEach((item: any) => {
        try {
          item?.view?.dispose?.();
        } catch (_e) {
          // ignore remote view cleanup error
        }
        try {
          item?.renderer?.dispose?.();
        } catch (_e) {
          // ignore remote renderer cleanup error
        }
      });
      this.remoteRenderers = [];
      this.hasRemoteVideo = false;
      this.sessionStatus = "Connected. Waiting for participant video...";
    },
    async renderLocalPreview(this: any) {
      if (!this.localVideoStream || !this.$refs.localContainer) return;
      this.clearLocalPreview();
      this.localRenderer = new VideoStreamRenderer(this.localVideoStream);
      this.localView = await this.localRenderer.createView();
      (this.$refs.localContainer as HTMLElement).appendChild(this.localView.target);
      this.isCameraOn = true;
      this.localPreviewStreamType = "Video";
    },
    async toggleMute(this: any) {
      if (!this.currentCall) return;
      try {
        if (this.isMuted) {
          await this.currentCall.unmute();
          this.isMuted = false;
        } else {
          await this.currentCall.mute();
          this.isMuted = true;
        }
      } catch (e: any) {
        this.set_root_notification(e?.message || "Unable to change microphone state.");
      }
    },
    async toggleCamera(this: any) {
      if (!this.currentCall || !this.deviceManager) return;
      try {
        if (!this.localVideoStream) {
          const cameras = await this.deviceManager.getCameras();
          if (!cameras?.length) {
            this.set_root_notification("No camera device found.");
            return;
          }
          this.localVideoStream = new LocalVideoStream(cameras[0]);
          await this.renderLocalPreview();
        }

        if (this.isCameraOn) {
          await this.currentCall.stopVideo(this.localVideoStream);
          this.isCameraOn = false;
          this.clearLocalPreview();
        } else {
          await this.currentCall.startVideo(this.localVideoStream);
          this.isCameraOn = true;
          await this.renderLocalPreview();
        }
      } catch (e: any) {
        this.set_root_notification(e?.message || "Unable to change camera state.");
      }
    },
    async toggleScreenShare(this: any) {
      if (!this.currentCall) return;
      try {
        if (this.isScreenSharing) {
          await this.currentCall.stopScreenSharing();
          this.isScreenSharing = false;
          if (this.localVideoStream && this.isCameraOn) {
            await this.renderLocalPreview();
          }
        } else {
          await this.currentCall.startScreenSharing();
          this.isScreenSharing = true;
          this.sessionStatus = "Screen sharing is active.";
        }
      } catch (e: any) {
        this.set_root_notification(
          e?.message || "Screen share is unavailable in this browser/device."
        );
      }
    },
    async renderRemoteStream(this: any, remoteStream: any) {
      if (!remoteStream?.isAvailable || !this.$refs.remoteContainer) return;
      this.clearRemotePreview();
      const renderer = new VideoStreamRenderer(remoteStream);
      const view = await renderer.createView();
      const container = this.$refs.remoteContainer as HTMLElement;
      container.appendChild(view.target);
      this.remoteRenderers.push({ renderer, view });
      this.hasRemoteVideo = true;
      this.sessionStatus = "Connected";
    },
    subscribeToRemoteParticipant(this: any, participant: any) {
      participant.videoStreams.forEach((stream: any) => {
        stream.on("isAvailableChanged", async () => {
          if (stream.isAvailable) {
            await this.renderRemoteStream(stream);
          } else {
            this.clearRemotePreview();
          }
        });
        if (stream.isAvailable) {
          this.renderRemoteStream(stream);
        }
      });
    },
    getLatestValidInvite(this: any) {
      const candidateId = Number(this.candidateId || this.user?.id || 0);
      if (!candidateId) return null;
      const inviteId = String(this.$route?.query?.inviteId || "").trim();
      const routeJobId = Number(this.$route?.query?.jobId || 0);
      if (inviteId) {
        const exactInvite = (this.personInvites || []).find((i: any) => {
          if (Number(i?.candidate_id || 0) !== candidateId) return false;
          if (String(i?.id || "") !== inviteId) return false;
          if (routeJobId && Number(i?.job_id || 0) !== routeJobId) return false;
          return true;
        });
        if (exactInvite) return exactInvite;
      }
      const validInvites = (this.personInvites || []).filter(
        (i: any) =>
          i?.candidate_id === candidateId &&
          i?.status === "scheduled" &&
          i?.scheduled_at &&
          !isNaN(new Date(i.scheduled_at).getTime())
      );
      validInvites.sort(
        (a: any, b: any) =>
          new Date(b.scheduled_at).getTime() - new Date(a.scheduled_at).getTime()
      );
      return validInvites[0] || null;
    },
    async ensureMeetingLink(this: any, maxAttempts = 3): Promise<boolean> {
      for (let attempt = 1; attempt <= maxAttempts; attempt += 1) {
        const invite = this.getLatestValidInvite();
        this.currentInvite = invite || this.currentInvite;
        this.meetingLink = String(invite?.ms_teams_link || "").trim();
        if (this.meetingLink) return true;
        if (attempt < maxAttempts) {
          this.sessionStatus = "Fetching meeting details...";
          await this.$store.dispatch(
            "recruiter_ai_interviews/" + INIT_AI_INTERVIEW_INVITES
          );
          await new Promise((resolve) => setTimeout(resolve, 700));
        }
      }
      return false;
    },
    async connectAcsTeamsCall(this: any) {
      if (this.isJoiningCall || this.currentCall) return;
      this.isJoiningCall = true;
      if (!this.meetingLink) {
        this.sessionError = "";
        const hasMeetingLink = await this.ensureMeetingLink(3);
        if (!hasMeetingLink) {
          this.sessionError = "Meeting link is not available.";
          this.isJoiningCall = false;
          return;
        }
      }
      try {
        this.sessionError = "";
        this.sessionStatus = "Getting secure access token...";
        const displayName = `${this.user?.first_name || "Candidate"} ${this.user?.last_name || ""}`.trim();
        const tokenResp = await generate_acs_teams_token({ display_name: displayName });
        const token = tokenResp?.data?.data?.token;
        if (!token) {
          this.sessionError = "Failed to get ACS token.";
          this.isJoiningCall = false;
          return;
        }

        const credential = new AzureCommunicationTokenCredential(token);
        const sharedClient = (window as any).__hcmsAcsCallClient || null;
        const sharedAgent = (window as any).__hcmsAcsCallAgent || null;
        this.callClient = sharedClient || new CallClient();
        if (!sharedClient) {
          (window as any).__hcmsAcsCallClient = this.callClient;
        }
        this.deviceManager = await this.callClient.getDeviceManager();
        await this.deviceManager.askDevicePermission({ audio: true, video: true });
        if (sharedAgent) {
          this.callAgent = sharedAgent;
        } else if (!this.callAgent) {
          this.callAgent = await this.callClient.createCallAgent(credential, {
            displayName
          });
          (window as any).__hcmsAcsCallAgent = this.callAgent;
        }

        const cameras = await this.deviceManager.getCameras();
        if (cameras?.length) {
          this.localVideoStream = new LocalVideoStream(cameras[0]);
          await this.renderLocalPreview();
        }

        this.sessionStatus = "Joining Teams meeting...";
        const options: any = {
          audioOptions: { muted: false }
        };
        if (this.localVideoStream) {
          options.videoOptions = { localVideoStreams: [this.localVideoStream] };
        }

        this.currentCall = this.callAgent.join(
          { meetingLink: this.meetingLink } as any,
          options
        );

        this.currentCall.on("stateChanged", () => {
          const state = this.currentCall?.state;
          if (state === "Connected") {
            this.sessionStatus = "Connected";
            this.startSpeechRecognition();
            return;
          }
          if (state === "InLobby") {
            this.sessionStatus =
              "Waiting in lobby. Interviewer must admit you from Teams.";
            return;
          }
          if (state === "Connecting") {
            this.sessionStatus = "Connecting to Teams meeting...";
            return;
          }
          if (state === "Disconnected") {
            this.stopSpeechRecognition();
            const endCode = this.currentCall?.callEndReason?.code;
            const endSubCode = this.currentCall?.callEndReason?.subCode;
            const reasonText =
              endCode || endSubCode
                ? ` (reason: ${endCode || "n/a"}:${endSubCode || "n/a"})`
                : "";
            this.sessionError = `Disconnected from meeting${reasonText}.`;
            this.leaveSession();
          }
        });
        this.currentCall.on("isMutedChanged", () => {
          this.isMuted = Boolean(this.currentCall?.isMuted);
        });
        this.currentCall.on("isScreenSharingOnChanged", () => {
          this.isScreenSharing = Boolean(this.currentCall?.isScreenSharingOn);
          if (!this.isScreenSharing && this.localVideoStream && this.isCameraOn) {
            this.renderLocalPreview();
          }
        });
        this.currentCall.on("localVideoStreamsUpdated", async (e: any) => {
          const addedStreams = e?.added || [];
          const screenStream = addedStreams.find(
            (s: any) => s?.mediaStreamType === "ScreenSharing"
          );
          if (screenStream && this.$refs.localContainer) {
            try {
              this.clearLocalPreview();
              this.localRenderer = new VideoStreamRenderer(screenStream);
              this.localView = await this.localRenderer.createView();
              (this.$refs.localContainer as HTMLElement).appendChild(
                this.localView.target
              );
              this.localPreviewStreamType = "ScreenSharing";
            } catch (_e) {
              // Screen preview may not be available on every browser
            }
          }
        });
        this.currentCall.on("remoteParticipantsUpdated", (e: any) => {
          (e.added || []).forEach((p: any) => this.subscribeToRemoteParticipant(p));
          if ((e.removed || []).length > 0) {
            this.clearRemotePreview();
          }
        });
        (this.currentCall.remoteParticipants || []).forEach((p: any) =>
          this.subscribeToRemoteParticipant(p)
        );
      } catch (e: any) {
        const errorMessage = e?.message || "";
        if (errorMessage.includes("already exists")) {
          // Reuse previously-created shared agent in this tab.
          try {
            this.callClient = (window as any).__hcmsAcsCallClient || new CallClient();
            (window as any).__hcmsAcsCallClient = this.callClient;
            this.deviceManager = await this.callClient.getDeviceManager();
            await this.deviceManager.askDevicePermission({ audio: true, video: true });
            this.callAgent = (window as any).__hcmsAcsCallAgent || null;
            if (!this.callAgent) {
              throw new Error("Call agent is still initializing. Please retry.");
            }
            this.currentCall = this.callAgent.join(
              { meetingLink: this.meetingLink } as any,
              { audioOptions: { muted: false } }
            );
            this.sessionStatus = "Connecting to Teams meeting...";
            return;
          } catch (_retryErr: any) {
            // fall through to generic handler
          }
        }
        this.sessionError = "Failed to join meeting from platform.";
        this.set_root_notification(
          e?.response?.data?.message || e?.message || "Failed to join meeting."
        );
      } finally {
        this.isJoiningCall = false;
      }
    }
  },
  async created(this: any) {
    await this.$store.dispatch("recruiter_ai_interviews/" + INIT_AI_INTERVIEW_INVITES);
    this.candidateId = this.user?.id || null;
    if (!this.candidateId) return;
    const invite = this.getLatestValidInvite();
    this.currentInvite = invite || null;
    this.meetingLink = String(invite?.ms_teams_link || "").trim();
    this.initSpeechRecognition();
  },
  async mounted(this: any) {
    await this.$nextTick();
    window.addEventListener("beforeunload", this.onPageExit);
    window.addEventListener("pagehide", this.onPageExit);
    await this.connectAcsTeamsCall();
  },
  async beforeDestroy(this: any) {
    window.removeEventListener("beforeunload", this.onPageExit);
    window.removeEventListener("pagehide", this.onPageExit);
    this.stopSpeechRecognition();
    this.persistTranscriptOnPageExit();
    await this.flushTranscriptToDb(true, "completed");
    if (this.transcriptFlushTimer) {
      clearTimeout(this.transcriptFlushTimer);
      this.transcriptFlushTimer = null;
    }
    await this.cleanupCallResources();
    this.remoteRenderers.forEach((item: any) => {
      try {
        item?.view?.dispose?.();
      } catch (_e) {
        // ignore cleanup errors
      }
      try {
        item?.renderer?.dispose?.();
      } catch (_e) {
        // ignore cleanup errors
      }
    });
    try {
      this.localView?.dispose?.();
      this.localRenderer?.dispose?.();
    } catch (_e) {
      // ignore
    }
  }
});
